import { Component, Input } from '@angular/core';
import * as am5 from "@amcharts/amcharts5";
import * as am5map from "@amcharts/amcharts5/map";
import am5geodata_worldLow from "@amcharts/amcharts5-geodata/worldLow";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { ApiService } from "../../Shared/api.service";
import { ToggleDarkModeService } from "../../Shared/toggle-dark-mode.service";
import { DarkTheme } from "@amcharts/amcharts5/.internal/themes/DarkTheme";

@Component({
  selector: 'bubble-city-map',
  standalone: false,
  templateUrl: './bubble-city-map.component.html',
  styleUrl: './bubble-city-map.component.css'
})
export class BubbleCityMapComponent {
  @Input() data: any;
  isDarkTheme: boolean = false;
  chartContainer: any;

  root: any;

  constructor(private api: ApiService, private helper: ToggleDarkModeService) {
  }

  ngOnInit(): void {
    // console.log("BubbleCityMapComponent initialized");
    this.root = am5.Root.new("chartdiv");
    this.helper.selected_mode$.subscribe(mode => {
      this.isDarkTheme = mode;
      this.change_theme(this.root);
  })
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    for (let group of this.data) {
      for (let item of group) {
        if (item.color && typeof item.color === 'string') {
          item.color = am5.color(item.color);
        }
      }
    }
    this.makeChart();
  }

  makeChart() {
    let root = this.root;
    let data = this.data;
    let currentIndex = 0;

    // Create main chart container (no vertical layout)
    let chartContainer = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    this.chartContainer = chartContainer;

    // Create Mercator chart (fills container)
    let mercatorChart = chartContainer.children.push(
      am5map.MapChart.new(root, {
        projection: am5map.geoMercator(),
        panX: "translateX",
        panY: "translateY",
        wheelY: "zoom",
        visible: true,
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    // Create Globe chart (fills container, initially hidden)
    let globeChart = chartContainer.children.push(
      am5map.MapChart.new(root, {
        projection: am5map.geoOrthographic(),
        panX: "rotateX",
        panY: "rotateY",
        wheelY: "zoom",
        visible: false,
        width: am5.percent(100),
        height: am5.percent(100)
      })
    );

    // --- Shared UI container (toggle) layered over the map, top left ---
    let sharedUI = chartContainer.children.push(
      am5.Container.new(root, {
        layout: root.horizontalLayout,
        x: 20,
        y: 40,
        centerX: am5.p0,
        centerY: am5.p0,
        paddingTop: 0,
        paddingBottom: 100,
        paddingLeft: 0,
        paddingRight: 0,
        position: "absolute"
      })
    );

    // Toggle (Map/Globe)
    sharedUI.children.push(am5.Label.new(root, {
      centerY: am5.p50,
      text: "Map",
      fill: this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000)
    }));

    let switchButton = sharedUI.children.push(am5.Button.new(root, {
      themeTags: ["switch"],
      centerY: am5.p50,
      icon: am5.Circle.new(root, {
        themeTags: ["icon"]
      })
    }));

    sharedUI.children.push(am5.Label.new(root, {
      centerY: am5.p50,
      text: "Globe",
      fill: this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000)
    }));

    // --- Legend container layered at the bottom of the map ---
    let legendContainer = chartContainer.children.push(
      am5.Container.new(root, {
        layout: root.horizontalLayout,
        x: 20,
        y: am5.percent(95), // start at bottom
        centerY: am5.p100,   // anchor to bottom
        position: "absolute"
      })
    );

    let legend = legendContainer.children.push(am5.Legend.new(root, {
      nameField: "name",
      fillField: "color",
      strokeField: "color",
      layout: root.horizontalLayout,
      centerY: am5.p50,
      marginLeft: 0
    }));

    legend.data.setAll([
      { name: "Severe", color: am5.color("#B50D12") },
      { name: "Very High", color: am5.color("#C94218") },
      { name: "High", color: am5.color("#E6A243") },
      { name: "Medium", color: am5.color("#009054") },
      { name: "Low", color: am5.color("#0080DC") }
    ]);

    // --- End shared UI ---

    function setupChart(chart: any, isGlobe: boolean) {
      let exclusions = !isGlobe ? ["AQ"] : [];
      let polygonSeries = chart.series.push(
        am5map.MapPolygonSeries.new(root, {
          geoJSON: am5geodata_worldLow,
          exclude: exclusions,
        })
      );

      polygonSeries.mapPolygons.template.setAll({
        interactive: true,
        strokeWidth: 0.5
      });

      // Add a background polygon covering the whole world
      let backgroundSeries = chart.series.unshift(
        am5map.MapPolygonSeries.new(root, {})
      );
      backgroundSeries.mapPolygons.template.setAll({
        fill: am5.color(0xd5deec), // ocean color
        stroke: am5.color(0xd5deec),
        fillOpacity: 1,
        strokeOpacity: 1
      });
      backgroundSeries.data.push({
        geometry: am5map.getGeoRectangle(90, 180, -90, -180)
      });

      let imageSeries = chart.series.push(
        am5map.MapPointSeries.new(root, {
          valueField: "value",
          calculateAggregates: true,
          latitudeField: "latitude",
          longitudeField: "longitude"
        })
      );

      let circleTemplate = am5.Template.new<am5.Circle>({});

      imageSeries.bullets.push((root: any, series: any, dataItem: any) => {
        let container = am5.Container.new(root, {});
        let circle = container.children.push(
          am5.Circle.new(root, {
            radius: 20,
            cursorOverStyle: "pointer",
            tooltipText: "[bold]{city_name}, {country_name}[/]\n{threat_type}",
            fill: dataItem.dataContext.color,
            fillOpacity: 1
          }, circleTemplate)
        );
        return am5.Bullet.new(root, {
          sprite: container,
          dynamic: true
        });
      });

      imageSeries.set("heatRules", [
        {
          target: circleTemplate,
          dataField: "value",
          min: 0,
          max: 13,
          minValue: 0,
          maxValue: 5,
          key: "radius"
        }
      ]);

      return imageSeries;
    }

    let mercatorSeries = setupChart(mercatorChart, false);
    let globeSeries = setupChart(globeChart, true);

    // Initial data
    mercatorSeries.data.setAll(data[0]);
    globeSeries.data.setAll(data[0]);

    // Update function
    const updateData = (chartSeries: any, isGlobe: boolean) => {
      if (isGlobe) {
        currentIndex = (currentIndex + 1) % data.length;
      }

      // Set all bubbles' value to 0 (make them disappear smoothly)
      chartSeries.data.each((item: any, i: number) => {
        chartSeries.data.setIndex(i, {
          latitude: item.latitude,
          longitude: item.longitude,
          value: 0,
          city_name: item.city_name,
          country_name: item.country_name,
          risk: "",
        });
      });

      setTimeout(() => {
        chartSeries.data.each((item: any, i: number) => {
          chartSeries.data.setIndex(i, {
            latitude: data[currentIndex][i].latitude,
            longitude: data[currentIndex][i].longitude,
            value: 0,
            city_name: item.city_name,
            country_name: item.country_name,
            risk: "",
          });
        });
      }, 1000)


      setTimeout(() => {
        data[currentIndex].forEach((item: any, i: number) => {
          chartSeries.data.setIndex(i, {
            latitude: item.latitude,
            longitude: item.longitude,
            value: item.value,
            city_name: item.city_name,
            country_name: item.country_name,
            risk: item.risk,
            threat_type: item.threat_type,
          });
        });
      }, 2000);
    };
    setInterval(() => updateData(mercatorSeries, false), 15000);
    setInterval(() => updateData(globeSeries, true), 15000);

    // Switch UI logic
    switchButton.on("active", function () {
      let isGlobe = switchButton.get("active");
      mercatorChart.set("visible", !isGlobe);
      globeChart.set("visible", isGlobe);
    });
  }

  private change_theme(root: any) {
    if (this.isDarkTheme) {
        root.setThemes([
            DarkTheme.new(root)
        ]);
    } else {
        root.setThemes([
            am5themes_Animated.new(root)
        ]);
    }

    this.updateLabelColors();
  }

  private updateLabelColors() {
    // Find and update the label colors in the chart container
    if (this.chartContainer) {
        this.chartContainer.children.each((child: any) => {
            if (child instanceof am5.Container) {
                child.children.each((grandChild: any) => {
                    if (grandChild instanceof am5.Label) {
                        grandChild.set("fill", this.isDarkTheme ? am5.color(0xffffff) : am5.color(0x000000));
                    }
                });
            }
        });
    }
  }
}
